import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import { imageStoreService } from '@/services/rest';
import type {
    ImageStorePayload,
    ImageStoreResponse,
} from '@/types/apps/image-store.type';

export function useEntityImageStore(entityName: string, entityId?: string) {
    const qc = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const key = ['imageStores', entityName, entityId] as const;

    const {
        data: items = [],
        isLoading,
        refetch,
    } = useQuery<ImageStoreResponse[], Error>({
        queryKey: key,
        queryFn: () =>
            imageStoreService()
                .index({ entityName, entityId: entityId ?? '' })
                .then(res => res.result.items),
        enabled: !!entityId,
        gcTime: 0,
        staleTime: 0,
    });

    const deleteMutation = useMutation<void, Error, string>({
        mutationFn: ids =>
            imageStoreService()
                .destroy([ids])
                .then(() => {}),
    });

    const createMutation = useMutation<
        ImageStoreResponse,
        Error,
        ImageStorePayload
    >({
        mutationFn: payload =>
            imageStoreService()
                .create(payload)
                .then(res => res.result),
    });

    const updateImage = async (
        file: File | string,
        customEntityId?: string
    ) => {
        try {
            const oldId = items[0]?.id;

            const targetEntityId =
                customEntityId !== undefined ? customEntityId : entityId;

            if (oldId) await deleteMutation.mutateAsync(oldId);
            await createMutation.mutateAsync({
                file,
                entityName,
                entityId: targetEntityId || '',
            });

            await qc.invalidateQueries({ queryKey: key });
        } catch {}
    };

    const isUpdating = deleteMutation.isPending || createMutation.isPending;

    return {
        items,
        url: items[0]?.url,
        isLoading,
        isUpdating,
        createImage: createMutation.mutate,
        deleteImage: deleteMutation.mutate,
        updateImage,
        refetch,
    };
}
